<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const isLoading = ref(false);
const isRestarting = ref(false);
const isLoggingOut = ref(false);
const qrCodeUrl = ref('');
const pairingCode = ref('');
const connectionState = ref(null);
const isCheckingConnection = ref(false);
const connectionCheckTimeout = ref(null);
const webhookConfigured = ref(false);

const generateInstanceName = (inboxName, accountId) => {
  // Simulate Ruby's parameterize(separator: '_') method
  const sanitizedName = inboxName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') // Replace non-alphanumeric with underscore
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  return `account_${accountId}_${sanitizedName}`;
};

const configureWebhook = async () => {
  if (webhookConfigured.value) return;

  try {
    // Get server URL from backend configuration
    const serverUrlResponse = await InboxesAPI.getServerUrl(props.inbox.id);
    const serverUrl = serverUrlResponse.data.server_url;

    // Get account ID from the URL or from a global store
    const accountId = window.location.pathname.split('/')[3];
    const instanceName = generateInstanceName(props.inbox.name, accountId);
    const webhookUrl = `${serverUrl}/chatwoot/webhook/${encodeURIComponent(instanceName)}`;

    // Update inbox channel with webhook URL
    await InboxesAPI.update(props.inbox.id, {
      channel: {
        webhook_url: webhookUrl
      }
    });

    webhookConfigured.value = true;
    useAlert('Webhook configured successfully');
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to configure webhook');
  }
};

const generateQrcode = async () => {
  isLoading.value = true;
  qrCodeUrl.value = '';
  pairingCode.value = '';
  
  // Clear any existing timeout
  if (connectionCheckTimeout.value) {
    clearTimeout(connectionCheckTimeout.value);
    connectionCheckTimeout.value = null;
  }
  
  try {
    const response = await InboxesAPI.generateQrcode(props.inbox.id);
    const { code, pairingCode: codePairing } = response.data;
    
    if (code) {
      const qrCodeDataUrl = await QRCode.toDataURL(code, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
      qrCodeUrl.value = qrCodeDataUrl;
      pairingCode.value = codePairing || '';
      useAlert('QR code generated successfully');
      
      // Schedule connection check after 20 seconds
      connectionCheckTimeout.value = setTimeout(async () => {
        await checkConnection();
        if (getConnectionStatus.value === 'connected') {
          qrCodeUrl.value = '';
          pairingCode.value = '';
        }
      }, 20000);
    }
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to generate QR code');
  } finally {
    isLoading.value = false;
  }
};

const restartInstance = async () => {
  isRestarting.value = true;
  
  try {
    await InboxesAPI.restartInstance(props.inbox.id);
    useAlert('Instance restarted successfully');
    checkConnection();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to restart instance');
  } finally {
    isRestarting.value = false;
  }
};

const logoutInstance = async () => {
  isLoggingOut.value = true;
  
  try {
    await InboxesAPI.logoutInstance(props.inbox.id);
    useAlert('Instance logged out successfully');
    checkConnection();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to logout instance');
  } finally {
    isLoggingOut.value = false;
  }
};

const checkConnection = async () => {
  isCheckingConnection.value = true;
  
  try {
    const response = await InboxesAPI.getConnectionState(props.inbox.id);
    const state = response.data?.state || response.data?.instance?.state || response.data?.connectionState;
    connectionState.value = state;
    
    // If connected, hide QR code and configure webhook
    const status = state?.toLowerCase();
    if (status === 'open' || status === 'connected') {
      qrCodeUrl.value = '';
      pairingCode.value = '';
      if (connectionCheckTimeout.value) {
        clearTimeout(connectionCheckTimeout.value);
        connectionCheckTimeout.value = null;
      }

      // Configure webhook when connection is established
      await configureWebhook();
    }
  } catch (error) {
    connectionState.value = 'close';
  } finally {
    isCheckingConnection.value = false;
  }
};

const getConnectionStatus = computed(() => {
  if (!connectionState.value) return null;
  const state = connectionState.value.toLowerCase();
  if (state === 'open' || state === 'connected') return 'connected';
  if (state === 'connecting') return 'connecting';
  return 'disconnected';
});

const getStatusColor = computed(() => {
  const status = getConnectionStatus.value;
  if (status === 'connected') return 'bg-green-500';
  if (status === 'connecting') return 'bg-yellow-500';
  return 'bg-red-500';
});

const getStatusText = computed(() => {
  const status = getConnectionStatus.value;
  if (status === 'connected') return t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTED') || 'Connected';
  if (status === 'connecting') return t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTING') || 'Connecting';
  return t('INBOX_MGMT.QRCODE_CONNECTOR.DISCONNECTED') || 'Disconnected';
});

onMounted(() => {
  checkConnection();
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.TABS.QRCODE_CONNECTOR')"
      :sub-title="$t('INBOX_MGMT.QRCODE_CONNECTOR.SUB_TITLE')"
    >
      <div class="flex flex-col gap-6">
        <div v-if="connectionState !== null" class="flex items-center gap-2 mb-2">
          <div
            class="w-3 h-3 rounded-full"
            :class="getStatusColor"
          />
          <span class="text-sm text-slate-600 dark:text-slate-400">
            {{ getStatusText }}
          </span>
        </div>
        <div class="flex justify-start items-center gap-3">
          <NextButton :is-loading="isLoading" @click="generateQrcode">
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.GENERATE_BUTTON') }}
          </NextButton>
          <NextButton color="amber" :is-loading="isRestarting" @click="restartInstance">
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.RESTART_BUTTON') }}
          </NextButton>
          <NextButton color="ruby" :is-loading="isLoggingOut" @click="logoutInstance">
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.LOGOUT_BUTTON') }}
          </NextButton>
        </div>
        
        <div v-if="qrCodeUrl" class="flex flex-col items-center gap-6 mt-2">
          <div class="w-full max-w-md text-center">
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.SCAN_INSTRUCTION') || 'Scan this QR code with your device to connect' }}
            </p>
          </div>
          <div class="bg-white dark:bg-slate-800 p-8 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
            <div class="flex items-center justify-center bg-white dark:bg-slate-900 p-4 rounded-lg">
              <img :src="qrCodeUrl" alt="QR Code" class="w-64 h-64" />
            </div>
          </div>
          
          <div v-if="pairingCode" class="w-full max-w-md">
            <div class="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <p class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.PAIRING_CODE_LABEL') || 'Pairing Code' }}
              </p>
              <div class="flex items-center gap-2">
                <code class="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm font-mono text-slate-900 dark:text-slate-100 break-all">
                  {{ pairingCode }}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>

